import {
    Badge,
    Box,
    Button,
    Flex,
    FormControl,
    FormLabel,
    HStack,
    Input,
    NumberDecrementStepper,
    NumberIncrementStepper,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    Select,
    SimpleGrid,
    Spinner,
    Text,
    Textarea,
    VStack,
    useToast,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import ErrorState from '../../../components/states/ErrorState';
import InlineErrorState from '../../../components/states/InlineErrorState';
import PageCard from '../../../components/page-card';
import { translate } from '../../../i18n';
import {
    AchievementCategory,
    AchievementCriteriaType,
    AchievementDefinitionDto,
    AchievementRewardType,
    AchievementTier,
    StreakActivityType,
    StreakPeriodType,
    UpdateAchievementDefinitionDto,
} from '../../../models/achievement/dto/achievement.dto';
import { useListCoupons } from '../../coupon-management/hooks/useListCoupons';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import ScopePrompt from '../../_shared/ScopePrompt';
import { classifyRead } from '../../_shared/readState';
import { useListAchievements } from '../hooks/useListAchievements';
import { useUpdateAchievement } from '../hooks/useUpdateAchievement';
import { isUnreadable } from '../../analytics/utils/statValue';

const CRITERIA_TYPES: { value: AchievementCriteriaType; label: string }[] = [
    { value: 'games_played', label: 'Games Played' },
    { value: 'courts_booked', label: 'Courts Booked' },
    { value: 'events_joined', label: 'Events Joined' },
    { value: 'coaching_sessions', label: 'Coaching Sessions' },
    { value: 'matches_won', label: 'Matches Won' },
    { value: 'streak', label: 'Streak' },
    { value: 'unique_opponents', label: 'Unique Opponents' },
    { value: 'level_reached', label: 'Level Reached' },
];

const TIER_COLORS: Record<string, string> = {
    BRONZE: 'orange',
    SILVER: 'gray',
    GOLD: 'yellow',
    PLATINUM: 'purple',
};

export default function AchievementDetailPage() {
    const { achievementId } = useParams<{ achievementId: string }>();
    const { selectedOrganization } = useSelectedOrganization();
    // `useListAchievements` is `enabled: !!organizationId`, and React Query v4
    // reports a DISABLED query as `isLoading: true` / `fetchStatus: 'idle'`.
    // This page DID carry a scope guard — and it was fused into the loading
    // condition (`if (isLoading || !selectedOrganization) return <Spinner/>`),
    // so its arm WAS the loading affordance: an operator with no organization
    // selected got a permanent full-page spinner FROM the guard that looks
    // like it prevents one. D64@r106's third shape, second instance;
    // `AchievementListPage` was the first, on the same line of the same
    // feature, and this file was found by the source-level pin written there.
    //
    // Note what the OBVIOUS repair would have produced, because on a detail
    // page it is worse than the spinner: swapping the raw flag for
    // `isInitialLoading` alone drops a never-issued read straight through to
    // the `!achievement` arm below, which tells the operator this achievement
    // does not exist. That is the `TimeWindowGroupList` trap (R105-1) — one
    // repair produces the other — and it is why the classifier is needed
    // rather than a flag swap.
    const { data: achievements, isInitialLoading, isError, refetch } = useListAchievements(selectedOrganization?.id || '');
    const { mutateAsync: updateAchievement, isLoading: isUpdating } = useUpdateAchievement();
    const toast = useToast();

    const achievement = achievements?.find((a) => a.id === achievementId);

    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState<Partial<AchievementDefinitionDto>>({});

    useEffect(() => {
        if (achievement) {
            setFormData({ ...achievement });
        }
    }, [achievement]);

    const handleSave = async () => {
        if (!achievementId) return;

        // Validate streak criteria requires periodType
        if (formData.criteria?.type === 'streak' && !formData.criteria?.periodType) {
            toast({ title: translate('achievement.period_type_required'), status: 'error', duration: 5000, isClosable: true });
            return;
        }

        // Validate wallet credits requires rewardValue > 0
        if (formData.rewardType === AchievementRewardType.WALLET_CREDITS && (!formData.rewardValue || formData.rewardValue <= 0)) {
            toast({ title: translate('achievement.reward_value_required'), status: 'error', duration: 5000, isClosable: true });
            return;
        }

        const payload: { achievementId: string } & Partial<UpdateAchievementDefinitionDto> = { achievementId: achievementId! };
        if (formData.name !== undefined) payload.name = formData.name;
        if (formData.description !== undefined) payload.description = formData.description;
        if (formData.category !== undefined) payload.category = formData.category;
        if (formData.tier !== undefined) payload.tier = formData.tier;
        if (formData.criteria !== undefined) payload.criteria = formData.criteria;
        // sportType is cosmetic; the evaluated filter is criteria.sport (included via criteria below)
        if (formData.iconUrl !== undefined) payload.iconUrl = formData.iconUrl || undefined;
        if (formData.sortOrder !== undefined) payload.sortOrder = isNaN(formData.sortOrder) ? 0 : formData.sortOrder;
        if (formData.rewardType !== undefined) payload.rewardType = formData.rewardType;
        if (formData.rewardType === AchievementRewardType.WALLET_CREDITS) {
            payload.rewardValue = formData.rewardValue;
        } else if (formData.rewardType === AchievementRewardType.COUPON) {
            payload.rewardCouponId = formData.rewardCouponId;
        }
        try {
            await updateAchievement(payload as { achievementId: string } & UpdateAchievementDefinitionDto);
            toast({
                title: translate('achievement.updated_successfully'),
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            setIsEditing(false);
        } catch (error: any) {
            toast({
                title: translate('achievement.update_error'),
                description: error.message || '',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const readState = classifyRead(isError, isInitialLoading, achievements);

    if (readState === 'never-issued') {
        return <ScopePrompt gate='organization' />;
    }

    if (readState === 'loading') {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <Spinner size="lg" />
            </Box>
        );
    }

    // `unreadable`, not the raw `isError`: `useListAchievements` sets
    // `keepPreviousData`, so a background refetch that fails over an
    // achievement already on screen keeps it rather than replacing the whole
    // page with an alert (R96-3).
    if (readState === 'unreadable') {
        return (
            <ErrorState message={translate('achievement.load_error')} onRetry={refetch} />
        );
    }

    // Reached only when the read ANSWERED. That ordering is the whole point:
    // this arm makes a definitive claim about an id, and it must never be
    // reached by a read that was never issued.
    if (!achievement) {
        return (
            <ErrorState message={translate('achievement.not_found')} onRetry={refetch} />
        );
    }

    return (
        <VStack spacing={6} align="stretch">
            <PageCard
                showBackButton
                title={translate('achievement.achievement_details')}
                rightHeader={
                    isEditing ? (
                        <HStack>
                            <Button size="sm" onClick={() => { setIsEditing(false); setFormData({ ...achievement }); }}>
                                {translate('common.cancel')}
                            </Button>
                            <Button size="sm" colorScheme="brand" onClick={handleSave} isLoading={isUpdating}>
                                {translate('common.save')}
                            </Button>
                        </HStack>
                    ) : (
                        <Button size="sm" colorScheme="brand" variant="outline" onClick={() => setIsEditing(true)}>
                            {translate('achievement.edit_achievement')}
                        </Button>
                    )
                }
            >
                {isEditing ? (
                    <EditForm formData={formData} setFormData={setFormData} />
                ) : (
                    <AchievementInfo achievement={achievement} />
                )}
            </PageCard>
        </VStack>
    );
}

// ─── Read-only View ────────────────────────────────────────────

function AchievementInfo({ achievement }: { achievement: AchievementDefinitionDto }) {
    return (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} padding={6}>
            <VStack align="start" spacing={3}>
                <InfoRow label={translate('achievement.name')} value={achievement.name} />
                <InfoRow label={translate('achievement.description')} value={achievement.description || '—'} />
                <HStack>
                    <Text fontWeight="bold" minWidth="120px">{translate('achievement.category')}:</Text>
                    <Badge colorScheme="brand">{achievement.category}</Badge>
                </HStack>
                <HStack>
                    <Text fontWeight="bold" minWidth="120px">{translate('achievement.tier')}:</Text>
                    {achievement.tier ? (
                        <Badge colorScheme={TIER_COLORS[achievement.tier] || 'gray'}>{achievement.tier}</Badge>
                    ) : (
                        <Text color="gray.400">—</Text>
                    )}
                </HStack>
            </VStack>
            <VStack align="start" spacing={3}>
                <InfoRow
                    label={translate('achievement.criteria')}
                    value={`${achievement.criteria?.type?.replace(/_/g, ' ') ?? '—'} ≥ ${achievement.criteria?.threshold ?? '?'}${achievement.criteria?.sport ? ` (${achievement.criteria.sport})` : ''}`}
                />
                <HStack>
                    <Text fontWeight="bold" minWidth="120px">{translate('achievement.active')}:</Text>
                    <Badge colorScheme={achievement.isActive ? 'green' : 'red'}>
                        {achievement.isActive ? translate('achievement.active') : translate('achievement.inactive')}
                    </Badge>
                </HStack>
                <InfoRow label={translate('achievement.sort_order')} value={String(achievement.sortOrder)} />
                <InfoRow label={translate('achievement.sport_type')} value={achievement.sportType || '—'} />
                <HStack>
                    <Text fontWeight="bold" minWidth="120px">{translate('achievement.reward_type')}:</Text>
                    <Badge colorScheme={achievement.rewardType === AchievementRewardType.WALLET_CREDITS ? 'green' : achievement.rewardType === AchievementRewardType.COUPON ? 'brand' : 'gray'}>
                        {achievement.rewardType || 'NONE'}
                    </Badge>
                </HStack>
                {achievement.rewardType === AchievementRewardType.WALLET_CREDITS && achievement.rewardValue != null && (
                    <InfoRow label={translate('achievement.reward_credit_amount')} value={String(achievement.rewardValue)} />
                )}
                {achievement.rewardType === AchievementRewardType.COUPON && achievement.rewardCouponId && (
                    <InfoRow label={translate('achievement.reward_coupon')} value={achievement.rewardCouponId} />
                )}
            </VStack>
        </SimpleGrid>
    );
}

// ─── Edit Form ─────────────────────────────────────────────────

function EditForm({
    formData,
    setFormData,
}: {
    formData: Partial<AchievementDefinitionDto>;
    setFormData: React.Dispatch<React.SetStateAction<Partial<AchievementDefinitionDto>>>;
}) {
    const { selectedOrganization } = useSelectedOrganization();
    // A refused read used to render the placeholder option, i.e. "no coupon
    // chosen", for an achievement that HAS one — and left the operator no way
    // to change it. See `coupon-read-error-gate.test.ts`.
    const { data: coupons, isError: isCouponsError } = useListCoupons(selectedOrganization?.id || '');

    const handleChange = (field: string, value: any) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleCriteriaChange = (field: string, value: any) => {
        setFormData((prev) => ({
            ...prev,
            criteria: { ...prev.criteria!, [field]: value },
        }));
    };

    return (
        <Flex direction="column" gap={4} padding={6}>
            <FormControl>
                <FormLabel>{translate('achievement.name')}</FormLabel>
                <Input
                    value={formData.name || ''}
                    onChange={(e) => handleChange('name', e.target.value)}
                    maxLength={200}
                />
            </FormControl>

            <FormControl>
                <FormLabel>{translate('achievement.description')}</FormLabel>
                <Textarea
                    value={formData.description || ''}
                    onChange={(e) => handleChange('description', e.target.value)}
                    maxLength={1000}
                    rows={3}
                />
            </FormControl>

            <SimpleGrid columns={2} spacing={4}>
                <FormControl>
                    <FormLabel>{translate('achievement.category')}</FormLabel>
                    <Select
                        value={formData.category || ''}
                        onChange={(e) => handleChange('category', e.target.value)}
                    >
                        {Object.values(AchievementCategory).map((cat) => (
                            <option key={cat} value={cat}>{cat}</option>
                        ))}
                    </Select>
                </FormControl>

                <FormControl>
                    <FormLabel>{translate('achievement.tier')}</FormLabel>
                    <Select
                        value={formData.tier || ''}
                        onChange={(e) => handleChange('tier', e.target.value || undefined)}
                        placeholder={translate('achievement.no_tier')}
                    >
                        {Object.values(AchievementTier).map((tier) => (
                            <option key={tier} value={tier}>{tier}</option>
                        ))}
                    </Select>
                </FormControl>
            </SimpleGrid>

            <SimpleGrid columns={2} spacing={4}>
                <FormControl>
                    <FormLabel>{translate('achievement.criteria_type')}</FormLabel>
                    <Select
                        value={formData.criteria?.type || 'games_played'}
                        onChange={(e) => handleCriteriaChange('type', e.target.value)}
                    >
                        {CRITERIA_TYPES.map((ct) => (
                            <option key={ct.value} value={ct.value}>{ct.label}</option>
                        ))}
                    </Select>
                </FormControl>

                <FormControl>
                    <FormLabel>{translate('achievement.threshold')}</FormLabel>
                    <NumberInput
                        value={formData.criteria?.threshold || 1}
                        onChange={(_, val) => handleCriteriaChange('threshold', isNaN(val) ? 1 : Math.round(val))}
                        min={1}
                    >
                        <NumberInputField />
                        <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                        </NumberInputStepper>
                    </NumberInput>
                </FormControl>
            </SimpleGrid>

            {formData.criteria?.type === 'streak' && (
                <SimpleGrid columns={2} spacing={4}>
                    <FormControl>
                        <FormLabel>{translate('achievement.streak_activity_type')}</FormLabel>
                        <Select
                            value={formData.criteria?.activityType || ''}
                            onChange={(e) => handleCriteriaChange('activityType', e.target.value || undefined)}
                        >
                            <option value="">Any Activity</option>
                            {Object.values(StreakActivityType).map((at) => (
                                <option key={at} value={at}>{at}</option>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl>
                        <FormLabel>{translate('achievement.streak_period_type')}</FormLabel>
                        <Select
                            value={formData.criteria?.periodType || ''}
                            onChange={(e) => handleCriteriaChange('periodType', e.target.value || undefined)}
                        >
                            {Object.values(StreakPeriodType).map((pt) => (
                                <option key={pt} value={pt}>{pt}</option>
                            ))}
                        </Select>
                    </FormControl>
                </SimpleGrid>
            )}

            <SimpleGrid columns={2} spacing={4}>
                <FormControl>
                    <FormLabel>{translate('achievement.sport_filter')}</FormLabel>
                    <Input
                        value={formData.criteria?.sport || ''}
                        onChange={(e) => handleCriteriaChange('sport', e.target.value || undefined)}
                        placeholder={translate('achievement.sport_filter_placeholder')}
                    />
                </FormControl>

                <FormControl>
                    <FormLabel>{translate('achievement.icon_url')}</FormLabel>
                    <Input
                        value={formData.iconUrl || ''}
                        onChange={(e) => handleChange('iconUrl', e.target.value)}
                        placeholder="https://..."
                    />
                </FormControl>
            </SimpleGrid>

            <FormControl>
                <FormLabel>{translate('achievement.sort_order')}</FormLabel>
                <NumberInput
                    value={formData.sortOrder ?? 0}
                    onChange={(_, val) => handleChange('sortOrder', isNaN(val) ? 0 : val)}
                    min={0}
                >
                    <NumberInputField />
                    <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                    </NumberInputStepper>
                </NumberInput>
            </FormControl>

            <FormControl>
                <FormLabel>{translate('achievement.reward_type')}</FormLabel>
                <Select
                    value={formData.rewardType || AchievementRewardType.NONE}
                    onChange={(e) => handleChange('rewardType', e.target.value)}
                >
                    <option value={AchievementRewardType.NONE}>{translate('achievement.reward_none')}</option>
                    <option value={AchievementRewardType.WALLET_CREDITS}>{translate('achievement.reward_wallet_credits')}</option>
                    <option value={AchievementRewardType.COUPON}>{translate('achievement.reward_coupon')}</option>
                </Select>
            </FormControl>

            {formData.rewardType === AchievementRewardType.WALLET_CREDITS && (
                <FormControl>
                    <FormLabel>{translate('achievement.reward_credit_amount')}</FormLabel>
                    <NumberInput
                        value={formData.rewardValue ?? 0}
                        onChange={(_, val) => handleChange('rewardValue', isNaN(val) ? 0 : Math.round(val))}
                        min={0}
                    >
                        <NumberInputField />
                        <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                        </NumberInputStepper>
                    </NumberInput>
                </FormControl>
            )}

            {formData.rewardType === AchievementRewardType.COUPON && (
                <FormControl>
                    <FormLabel>{translate('achievement.reward_coupon_select')}</FormLabel>
                    {/* `isUnreadable`, not the raw flag: React Query KEEPS `coupons` through
                        a failed background refetch, and this arm REPLACES the control. Reading
                        the flag alone took a working reward-coupon picker
                        away from the operator because a refresh was refused
                        (useListCoupons, staleTime 0 — R132-1). */}
                    {isUnreadable(isCouponsError, coupons) ? (
                        <InlineErrorState message={translate('common.unexpected_error')} />
                    ) : (
                        <Select
                            value={formData.rewardCouponId || ''}
                            onChange={(e) => handleChange('rewardCouponId', e.target.value)}
                            placeholder={translate('achievement.reward_coupon_placeholder')}
                        >
                            {coupons?.map((coupon) => (
                                <option key={coupon.id} value={coupon.id}>{coupon.name}</option>
                            ))}
                        </Select>
                    )}
                </FormControl>
            )}
        </Flex>
    );
}

// ─── InfoRow ───────────────────────────────────────────────────

function InfoRow({ label, value }: { label: string; value: string }) {
    return (
        <HStack>
            <Text fontWeight="bold" minWidth="120px">{label}:</Text>
            <Text>{value}</Text>
        </HStack>
    );
}
